/**
 * What the address screen sends to the server once it is happy.
 *
 * Both ends of the shipment post to the same endpoint, so the loading
 * capabilities have to be filed under the pickup or the delivery half of the
 * request depending on which end this screen is. A location that doesn't need
 * a contact sends a blank one rather than whatever the form happens to hold.
 */

import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import type { SetAddressAndContactOperationRequest } from "@freightsimple/generated-dashboard-openapi-client/apis/ShipmentsApi";
import { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";

interface BookShipmentAddressAndContactRequestInput {
  shipmentId: string;
  context: LocationContext;
  location: PreQuoteLocation;
  contact: Contact;
  isContactNotRequired: boolean;
  referenceNumber: string;
  amazonReferenceId: string | undefined;
  amazonShipmentId: string | undefined;
  boothNumber: string;
  deadline: string | undefined;
  // Undefined when the customer never touched the switch, which sends no
  // opinion at all rather than asserting a state we only inferred.
  markLocationAsFavourite: boolean | undefined;
  locationHasDock: LoadingCapabilitiesAvailability | undefined;
  locationHasForklift: LoadingCapabilitiesAvailability | undefined;
}

const blankContact: Contact = {
  contactName: "",
  phoneNumber: "",
  emailAddress: "",
  phoneNumberExtension: "",
};

export function bookShipmentAddressAndContactRequest(
  input: BookShipmentAddressAndContactRequestInput,
): SetAddressAndContactOperationRequest {
  const isPickup = input.context === LocationContext.Pickup;

  // The Amazon ids are only handed in for an Amazon warehouse delivery. The
  // server owns packing them into the carrier-facing reference, so the packed
  // string is not sent alongside them.
  const isAmazonDelivery =
    input.amazonReferenceId !== undefined ||
    input.amazonShipmentId !== undefined;

  // TODO: Validate that we can make this type cast
  return {
    setAddressAndContactRequest: {
      shipmentId: input.shipmentId,
      context: input.context,
      // isScreenCompletelyEntered should have done sufficient verifications to make these casts safe
      location: input.location as Location,
      contact: input.isContactNotRequired ? blankContact : input.contact,
      referenceNumber: isAmazonDelivery ? undefined : input.referenceNumber,
      amazonReferenceId: input.amazonReferenceId,
      amazonShipmentId: input.amazonShipmentId,
      boothNumber: input.boothNumber,
      deadline: input.deadline,
      markLocationAsFavourite: input.markLocationAsFavourite,
      // We only want to do this for the pickup location in getting started
      markLocationAsDefault: false,
      // Loading Capabilities - sent based on context
      pickupLocationHasDock: isPickup ? input.locationHasDock : undefined,
      pickupLocationHasForklift: isPickup
        ? input.locationHasForklift
        : undefined,
      deliveryLocationHasDock: isPickup ? undefined : input.locationHasDock,
      deliveryLocationHasForklift: isPickup
        ? undefined
        : input.locationHasForklift,
    },
  } as SetAddressAndContactOperationRequest;
}
